import dynamic from 'next/dynamic';
import HomeHero from './HomeHero/index';
import { createElement, Fragment } from 'react';

/**
 * Generic Modules (Not Model Specific)
 */
const Icon = dynamic(() => import('./Icon'));
const Well = dynamic(() => import('./Well'));
const Quote = dynamic(() => import('./Quote'));
const Image = dynamic(() => import('./Image'));
const Alert = dynamic(() => import('./Alert'));
const Button = dynamic(() => import('./Button'));
const Content = dynamic(() => import('./Content'));
const AppHero = dynamic(() => import('./AppHero'));
const ChatCard = dynamic(() => import('./ChatCard'));
const LoanHero = dynamic(() => import('./LoanHero'));
const PostList = dynamic(() => import('./PostList'));
const PageHero = dynamic(() => import('./PageHero'));
const IconItem = dynamic(() => import('./IconItem'));
const SaveHero = dynamic(() => import('./SaveHero'));
const BoardList = dynamic(() => import('./BoardList'));
const PreFooter = dynamic(() => import('./PreFooter'));
const Thumbnail = dynamic(() => import('./Thumbnail'));
const Band = dynamic(() => import('./Band/BandModule'));
const SignupForm = dynamic(() => import('./SignupForm'));
const BackButton = dynamic(() => import('./BackButton'));
const StyledWell = dynamic(() => import('./StyledWell'));
const ReportCard = dynamic(() => import('./ReportCard'));
const HtmlEditor = dynamic(() => import('./HtmlEditor'));
const BlogNavbar = dynamic(() => import('./BlogNavbar'));
const Trustpilot = dynamic(() => import('./Trustpilot'));
const HomeHeroV3 = dynamic(() => import('./HomeHeroV3'));
const HelpSearch = dynamic(() => import('./HelpSearch'));
const MobileHero = dynamic(() => import('./MobileHero'));
const AwardsList = dynamic(() => import('./AwardsList'));
const CardButton = dynamic(() => import('./CardButton'));
const SwooshHero = dynamic(() => import('./SwooshHero'));
const Leadership = dynamic(() => import('./Leadership'));
const IconHeading = dynamic(() => import('./IconHeading'));
const ProductCard = dynamic(() => import('./ProductCard'));
const ProductHero = dynamic(() => import('./ProductHero'));
const SimpleBox = dynamic(() => import('./SimpleBox'));
const ReferralType = dynamic(() => import('./ReferralType'));
const ArticleQuote = dynamic(() => import('./ArticleQuote'));
const NumberedItem = dynamic(() => import('./NumberedItem'));
const FaqAccordion = dynamic(() => import('./FaqAccordion'));
const IconListItem = dynamic(() => import('./IconListItem'));
const YoutubeVideo = dynamic(() => import('./YoutubeVideo'));
const MaterialIcon = dynamic(() => import('./MaterialIcon'));
const IsaRatesTable = dynamic(() => import('./IsaRatesTable'));
const MediaFeatured = dynamic(() => import('./MediaFeatured'));
const Calculator = dynamic(() => import('./Calculator/index'));
const TelephoneCard = dynamic(() => import('./TelephoneCard'));
const CreditCardHero = dynamic(() => import('./CreditCardHero'));
const TrustPilotLogo = dynamic(() => import('./TrustpilotLogo'));
const GetZopaOnTheGo = dynamic(() => import('./GetZopaOnTheGo'));
const ImageButtonLink = dynamic(() => import('./ImageButtonLink'));
const ComparisonTable = dynamic(() => import('./ComparisonTable'));
const ProductTemplate = dynamic(() => import('./ProductTemplate'));
const AppStoreButtons = dynamic(() => import('./AppStoreButtons'));
const AppDownloadBanner = dynamic(() => import('./AppDownloadBanner'));
const BlogDownloadCard = dynamic(() => import('./BlogDownloadCard'));
const ButtonWithBadges = dynamic(() => import('./ButtonWithBadges'));
const ReferCapture = dynamic(() => import('./Forms/ReferCapture'));
const ReferAFriendHero = dynamic(() => import('./ReferAFriendHero'));
const CreditCardRepApr = dynamic(() => import('./CreditCardRepApr'));
const Strip = dynamic(() => import('@/components/Modules/Strip/Strip'));
const PricingTable = dynamic(() => import('@/components/Modules/PricingTable/PricingTable'));
const AppStoreModule = dynamic(() => import('./AppStoreModule'));
const InternalLinksModule = dynamic(() => import('./InternalLinksModule'));
const SavingsRatesTable = dynamic(() => import('./SavingsRatesTable'));
const TextAlignment = dynamic(() => import('./Alignment/TextAlignment'));
const BusinessEnq = dynamic(() => import('./BusinessEnquiries'));
const BoardModule = dynamic(() => import('./BoardModule'));
const FeaturedImageBand = dynamic(() => import('./FeaturedImageBand'));
const CurrentAccountHero = dynamic(() => import('./CurrentAccountHero'));
const ApplyHero = dynamic(() => import('./ApplyHero'));
const StickyFooterBanner = dynamic(() => import('./StickyFooterBanner'));
const ReferralTypeInvalid = dynamic(() => import('./ReferralTypeInvalid'));
const SupportCategoryList = dynamic(() => import('./SupportCategoryList'));
const SecondaryProductHero = dynamic(() => import('./SecondaryProductHero'));
const ContentAlongsideImage = dynamic(() => import('./ContentAlongsideImage'));
const SmartSaverHistoryTable = dynamic(() => import('./SmartSaverHistoryTable'));
const MobileNumberCapture = dynamic(() => import('./Forms/MobileNumberCapture'));
const LandingPageFaqAccordion = dynamic(() => import('./LandingPageFaqAccordion'));
const ComparisonTableVertical = dynamic(() => import('./ComparisonTableVertical'));
const EmailAttributionForm = dynamic(() => import('./Forms/EmailAttributionForm'));
const MobileAttributionForm = dynamic(() => import('./Forms/MobileAttributionForm'));
const SavingsClimbersRateTable = dynamic(() => import('./SavingsClimbersRateTable'));
const CalendlyWidget = dynamic(() => import('./Calendly/CalendlyWidget'));
const Experiment = dynamic(() => import('./Experiment'));

/**
 * B2B Specific Modules
 */

const B2BHero = dynamic(() => import('./B2BHero'));
const CaseStudiesForListPage = dynamic(() => import('./CaseStudies/Content'));
const CaseStudiesFilter = dynamic(() => import('./CaseStudies/Filter'));
const CaseStudyContent = dynamic(() => import('./CaseStudy/Content'));
const CardGrid = dynamic(() => import('./CardGrid/CardGrid'));
const Storyboard = dynamic(() => import('./Storyboard/Storyboard'));

/**
 * Grid
 */
const Row = dynamic(() => import('./Grid/Row'));

/**
 * Article Model Specific Modules
 */
const BlogImage = dynamic(() => import('./BlogImage'));
const PostYoutubeVideo = dynamic(() => import('./PostYoutubeVideo'));
const PostsSpotlight = dynamic(() => import('./PostsSpotlight'));
const PostsForAuthor = dynamic(() => import('./Author/Content'));
const ArticleContent = dynamic(() => import('./Article/Content'));
const PostsForCategory = dynamic(() => import('./Category/Content'));
const PostSearchResults = dynamic(() => import('./PostSearchResults'));
const PostsForAllPostsPage = dynamic(() => import('./AllPosts/Content'));

/**
 * Question Model Specific Modules
 */
const QuestionImage = dynamic(() => import('./QuestionImage'));
const QuestionHeader = dynamic(() => import('./Question/Header'));
const QuestionSurvey = dynamic(() => import('./Question/Survey'));
const QuestionUrgent = dynamic(() => import('./Question/Urgent'));
const QuestionRelated = dynamic(() => import('./Question/Related'));
const QuestionContent = dynamic(() => import('./Question/Content'));
const FaqSearchResults = dynamic(() => import('./FaqSearchResults'));
const QuestionNotification = dynamic(() => import('./Question/Notification'));

export type TypeName = Record<'__typename', string>;

type BuildElementProps<T, U> = {
  context?: Record<string, never> | U;
  module: T;
};

export const components = {
  SavingsClimbersRateTableRecord: SavingsClimbersRateTable,
  ComparisonTableVerticalRecord: ComparisonTableVertical,
  LandingPageFaqAccordionRecord: LandingPageFaqAccordion,
  SmartSaverHistoryTableRecord: SmartSaverHistoryTable,
  ContentAlongsideImageRecord: ContentAlongsideImage,
  MobileAttributionFormRecord: MobileAttributionForm,
  EmailAttributionFormRecord: EmailAttributionForm,
  PostsForAllPostsPageRecord: PostsForAllPostsPage,
  QuestionNotificationRecord: QuestionNotification,
  SecondaryProductHeroRecord: SecondaryProductHero,
  SupportCategoryListRecord: SupportCategoryList,
  MobileNumberCaptureRecord: MobileNumberCapture,
  ReferralTypeInvalidRecord: ReferralTypeInvalid,
  InternalLinksModuleRecord: InternalLinksModule,
  CaseStudiesListRecord: CaseStudiesForListPage,
  CurrentAccountHeroRecord: CurrentAccountHero,
  StickyFooterBannerRecord: StickyFooterBanner,
  CaseStudiesFilterRecord: CaseStudiesFilter,
  FeaturedImageBandRecord: FeaturedImageBand,
  SavingsRatesTableRecord: SavingsRatesTable,
  AppDownloadBannerRecord: AppDownloadBanner,
  PostSearchResultRecord: PostSearchResults,
  CreditCardRepAprRecord: CreditCardRepApr,
  PostsForCategoryRecord: PostsForCategory,
  ReferAFriendHeroRecord: ReferAFriendHero,
  BlogDownloadCardRecord: BlogDownloadCard,
  PostYoutubeVideoRecord: PostYoutubeVideo,
  CaseStudyDetailRecord: CaseStudyContent,
  ButtonWithBadgeRecord: ButtonWithBadges,
  FaqSearchResultRecord: FaqSearchResults,
  ComparisonTableRecord: ComparisonTable,
  ImageButtonLinkRecord: ImageButtonLink,
  ProductTemplateRecord: ProductTemplate,
  QuestionContentRecord: QuestionContent,
  QuestionRelatedRecord: QuestionRelated,
  CalendlyWidgetRecord: CalendlyWidget,
  ArticleContentRecord: ArticleContent,
  CreditCardHeroRecord: CreditCardHero,
  GetZopaOnTheGoRecord: GetZopaOnTheGo,
  PostsForAuthorRecord: PostsForAuthor,
  PostsSpotlightRecord: PostsSpotlight,
  QuestionHeaderRecord: QuestionHeader,
  QuestionSurveyRecord: QuestionSurvey,
  QuestionUrgentRecord: QuestionUrgent,
  TrustPilotLogoRecord: TrustPilotLogo,
  AppStoreModuleRecord: AppStoreModule,
  ReferMobileFormRecord: ReferCapture,
  IsaRatesTableRecord: IsaRatesTable,
  MediaFeaturedRecord: MediaFeatured,
  QuestionImageRecord: QuestionImage,
  TelephoneCardRecord: TelephoneCard,
  TextAlignmentRecord: TextAlignment,
  MaterialIconRecord: MaterialIcon,
  FaqAccordionRecord: FaqAccordion,
  IconListItemRecord: IconListItem,
  NumberedItemRecord: NumberedItem,
  YoutubeVideoRecord: YoutubeVideo,
  ReferralTypeRecord: ReferralType,
  PricingTableRecord: PricingTable,
  ArticleQuoteRecord: ArticleQuote,
  AppStoreRecord: AppStoreButtons,
  BoardModuleRecord: BoardModule,
  IconHeadingRecord: IconHeading,
  ProductCardRecord: ProductCard,
  ProductHeroRecord: ProductHero,
  BusinessEnqRecord: BusinessEnq,
  SignupFormRecord: SignupForm,
  HomeHeroV3Record: HomeHeroV3,
  ExperimentRecord: Experiment,
  AwardsListRecord: AwardsList,
  BackButtonRecord: BackButton,
  BlogNavbarRecord: BlogNavbar,
  CalculatorRecord: Calculator,
  CardButtonRecord: CardButton,
  HelpSearchRecord: HelpSearch,
  HtmlEditorRecord: HtmlEditor,
  LeadershipRecord: Leadership,
  MobileHeroRecord: MobileHero,
  ReportCardRecord: ReportCard,
  StyledWellRecord: StyledWell,
  SwooshHeroRecord: SwooshHero,
  TrustpilotRecord: Trustpilot,
  StoryboardRecord: Storyboard,
  ApplyHeroRecord: ApplyHero,
  SimpleBoxRecord: SimpleBox,
  BlogImageRecord: BlogImage,
  BoardListRecord: BoardList,
  PrefooterRecord: PreFooter,
  ThumbnailRecord: Thumbnail,
  ChatCardRecord: ChatCard,
  HomeHeroRecord: HomeHero,
  IconItemRecord: IconItem,
  LoanHeroRecord: LoanHero,
  PageHeroRecord: PageHero,
  PostListRecord: PostList,
  SaveHeroRecord: SaveHero,
  CardGridRecord: CardGrid,
  QuoteBlockRecord: Quote,
  AppHeroRecord: AppHero,
  ContentRecord: Content,
  B2bHeroRecord: B2BHero,
  ButtonRecord: Button,
  StripRecord: Strip,
  AlertRecord: Alert,
  ImageRecord: Image,
  BandRecord: Band,
  IconRecord: Icon,
  WellRecord: Well,
  RowRecord: Row,
};

type ModulesProps<T, U> = {
  context?: Record<string, never> | U;
  modules: T[];
};

export function buildModule<T extends TypeName, U = Record<string, never>>({
  context = {},
  module,
}: BuildElementProps<T, U>) {
  if (!module.__typename || !components[module.__typename as keyof typeof components]) {
    return null;
  }

  if (Object.hasOwn(module, 'pageContext')) {
    throw new Error(
      `[Modules - buildModule] - ${module.__typename} block cannot have a field with the name pageContext`,
    );
  }
  // @ts-expect-error not sure how to type the components here
  return createElement<T>(components[module.__typename], { ...module, pageContext: context });
}

export function Modules<T extends TypeName, U = Record<string, never>>({ context = {}, modules }: ModulesProps<T, U>) {
  return modules.map((module, index) => <Fragment key={index}>{buildModule<T, U>({ context, module })}</Fragment>);
}
